import { settingsNavPathV3 } from "@circle-react/helpers/urlHelpers";
import { useSearch } from "@tanstack/react-router";

import { t } from "@/i18n-js/instance";
import { useIsVideoAnalyticsEnabled } from "@/react/components/SettingsApp/Analytics/useIsVideoAnalyticsEnabled";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";

import { NavGroup } from "../NavGroup";
import { SubNavLinkItem } from "./SubNavLinkItem";
import type { Link } from "./types";

interface UseAnalyticsLinksOptions {
  enableAiAgentsAnalytics?: boolean;
}

export const useAnalyticsLinks = ({
  enableAiAgentsAnalytics = false,
}: UseAnalyticsLinksOptions = {}): Link[] => {
  const isVideoAnalyticsEnabled = useIsVideoAnalyticsEnabled();
  const { currentCommunitySettings } = usePunditUserContext();
  const isAiAgentsAnalyticsEnabled =
    Boolean(currentCommunitySettings?.ai_actions_analytics_dashboard_enabled) ||
    enableAiAgentsAnalytics;
  const links: Link[] = [
    {
      label: t("settings_nav_v3.analytics_v2.overview"),
      path: settingsNavPathV3.analytics_v2.index,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.members"),
      path: settingsNavPathV3.analytics_v2.members,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.website"),
      path: settingsNavPathV3.analytics_v2.website,
      exact: true,
    },
    ...(isVideoAnalyticsEnabled
      ? [
          {
            label: t("settings_nav_v3.analytics_v2.videos"),
            path: settingsNavPathV3.analytics_v2.videos,
            exact: true,
          },
        ]
      : []),
    {
      label: t("settings_nav_v3.analytics_v2.spaces"),
      path: settingsNavPathV3.analytics_v2.spaces,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.post_comments"),
      path: settingsNavPathV3.analytics_v2.post_comments,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.messages"),
      path: settingsNavPathV3.analytics_v2.messages,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.devices"),
      path: settingsNavPathV3.analytics_v2.devices,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.events"),
      path: settingsNavPathV3.analytics_v2.events,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.payments"),
      path: settingsNavPathV3.analytics_v2.payments,
      exact: true,
    },
    {
      label: t("settings_nav_v3.analytics_v2.courses"),
      path: settingsNavPathV3.analytics_v2.courses,
      exact: true,
    },
    ...(isAiAgentsAnalyticsEnabled
      ? [
          {
            label: t("settings_nav_v3.analytics_v2.ai_agents"),
            path: settingsNavPathV3.analytics_v2.ai_agents,
            exact: true,
            isNewFeature: new Date() < new Date("2026-06-20"),
          },
        ]
      : []),
    ...(currentCommunitySettings?.community_reviews_enabled
      ? [
          {
            label: t("settings_nav_v3.analytics_v2.reviews"),
            path: settingsNavPathV3.analytics_v2.reviews,
            exact: true,
          },
        ]
      : []),
  ];
  return links;
};

export const AnalyticsLinks = () => {
  /** @TODO When Analytics is migrated over, use Route.useSearch with type inference */
  const queryParams: any = useSearch({ strict: false });
  const isAiAgentsAnalyticsEnabledFromSearch =
    typeof queryParams === "object" &&
    queryParams !== null &&
    Reflect.get(queryParams, "ff_analytics_ai_agents") === true;
  const links = useAnalyticsLinks({
    enableAiAgentsAnalytics: isAiAgentsAnalyticsEnabledFromSearch,
  });

  return (
    <NavGroup groupLinks={links}>
      <NavGroup.Content title={t("settings_nav_v3.analytics_v2.label")}>
        {links.map(link => (
          <SubNavLinkItem
            key={link.label}
            label={link.label}
            to={link.path}
            search={queryParams}
            isNewFeature={link.isNewFeature}
          />
        ))}
      </NavGroup.Content>
    </NavGroup>
  );
};
